import React, { useCallback } from 'react';
import { Linking, StyleSheet, TouchableOpacity } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';

import { Stack } from '@/components/layout';
import { SheetContent, SheetModal, SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { BadgeName } from '@/features/player-session/types/types';
import { createStyles, useScaling } from '@/hooks';
import { colors } from '@/theme/colors';

type StepDetailNotesSheetProps = {
  open: boolean;
  levelNumber: number;
  skillName: string;
  levelColor: string;
  onOpenChange: (open: boolean) => void;
  activeBadge: {
    name: BadgeName;
    label: string;
  } | null;
};

const PATHWAY_NOTES_URL = 'https://foundation.uskidsgolf.com/L1-3';
const PATHWAY_NOTES_URL_DISPLAY = 'foundation.uskidsgolf.com/L1-3';

export function StepDetailNotesSheet({
  open,
  levelNumber,
  levelColor,
  onOpenChange,
  activeBadge,
}: StepDetailNotesSheetProps) {
  const styles = useStyles();
  const { scale, isTablet } = useScaling();

  const closeSheet = () => onOpenChange(false);

  const onPressLink = useCallback(async () => {
    const canOpen = await Linking.canOpenURL(PATHWAY_NOTES_URL);
    if (!canOpen) return;
    await Linking.openURL(PATHWAY_NOTES_URL);
  }, []);

  const renderInstructionLink = useCallback(() => {
    const full = STRINGS.playerSession.stepDetail.instructionLink;
    const idx = full.indexOf(PATHWAY_NOTES_URL_DISPLAY);
    if (idx === -1) {
      return (
        <Text
          fontWeight={'400'}
          fontSize={scale({ phone: 10, tablet: 12 })}
          fontFamily="Inter"
          style={styles.skillCardBody}>
          {full}
        </Text>
      );
    }
    const prefix = full.slice(0, idx);
    const suffix = full.slice(idx + PATHWAY_NOTES_URL_DISPLAY.length);
    return (
      <Text
        fontWeight={'400'}
        fontSize={scale({ phone: 10, tablet: 12 })}
        fontFamily="Inter"
        style={styles.skillCardBody}>
        {prefix}
        <Text style={[styles.linkText, { color: colors.Blue }]}>{PATHWAY_NOTES_URL_DISPLAY}</Text>
        {suffix}
      </Text>
    );
  }, [scale, styles.linkText, styles.skillCardBody]);

  return (
    <SheetModal open={open} onOpenChange={onOpenChange}>
      <SheetContent
        tabletWidth={scale(375)}
        closeOnBackdropPress={false}
        style={isTablet ? styles.sheet : [styles.sheet, styles.sheetPhone]}>
        <Stack style={styles.sheetContainer}>
          <Stack direction="row" align="center" style={styles.sheetHeader}>
            <TouchableOpacity onPress={closeSheet} hitSlop={scale(12)} style={styles.backButton}>
              <Stack direction="row" align="center" gap={scale(2)}>
                <SvgIcon name="IcBackBlue" size={24} />
                <Text
                  variant={
                    isTablet ? 'Font Inter/Subtitle/Regular 12px' : 'Font Inter/Subtitle/Medium'
                  }
                  style={styles.backText}>
                  {STRINGS.player.filters.back}
                </Text>
              </Stack>
            </TouchableOpacity>

            <Stack flex={1} align="center">
              <Text
                variant={
                  isTablet
                    ? 'Font Inter/Subtitle/Bold 14px'
                    : 'Font Montserrat/Subtitle/Minimize 1 Bold 11px'
                }
                fontWeight={isTablet ? undefined : '800'}
                style={styles.title}>
                {STRINGS.playerSession.stepDetail.pathwayNotesTitle}
              </Text>
            </Stack>

            <Stack style={styles.headerRightSpacer} />
          </Stack>

          <Stack style={styles.divider} />

          <ScrollView
            showsVerticalScrollIndicator={false}
            nestedScrollEnabled
            bounces
            alwaysBounceVertical
            keyboardShouldPersistTaps="handled"
            style={styles.scrollView}
            contentContainerStyle={styles.bodyContent}>
            <Text
              variant={
                isTablet
                  ? 'Font Inter/Subtitle/Bold 12px'
                  : 'Font Montserrat/Subtitle/Subtitle 2 Bold 12px'
              }
              fontWeight={isTablet ? undefined : '800'}
              style={[styles.levelHeader, { color: levelColor }]}>
              {STRINGS.playerSession.stepDetail.pathwayNotesLevelHeader(levelNumber)}
            </Text>
            <Stack direction="row" style={styles.bodyRow}>
              <Stack style={[styles.leftStripe, { backgroundColor: levelColor }]} />
              <Stack flex={1} style={styles.rightContent}>
                <Stack style={styles.card}>
                  <Stack paddingX={scale(8)} paddingY={scale(12)}>
                    <Text
                      fontWeight={'400'}
                      fontSize={scale(14)}
                      fontFamily="Inter"
                      style={styles.topicText}>
                      {activeBadge?.label}
                    </Text>
                  </Stack>

                  <Stack style={styles.cardDivider} />

                  <Stack paddingX={scale(16)} paddingY={scale(12)} gap={scale(2)}>
                    <Text
                      fontWeight={'700'}
                      fontSize={scale({ phone: 12, tablet: 14 })}
                      fontFamily="Inter"
                      style={[styles.skillCardTitle, { color: levelColor }]}>
                      {STRINGS.playerSession.stepDetail.skillCheckTitle}
                    </Text>
                    <Text
                      fontWeight={'700'}
                      fontSize={scale({ phone: 10, tablet: 12 })}
                      fontFamily="Inter"
                      style={styles.skillCardBody}>
                      {STRINGS.playerSession.stepDetail.putYourFormsToWork}
                    </Text>

                    <TouchableOpacity
                      activeOpacity={0.8}
                      onPress={onPressLink}
                      style={styles.linkPressable}>
                      {renderInstructionLink()}
                    </TouchableOpacity>

                    <Stack paddingTop={scale(15)} gap={scale(33)}>
                      <Stack gap={scale(0)}>
                        <Text
                          fontWeight={'700'}
                          fontSize={scale({ phone: 10, tablet: 12 })}
                          fontFamily="Inter"
                          style={styles.skillItemText}>
                          1. {STRINGS.playerSession.stepDetail.skill1Label}
                        </Text>
                        <TouchableOpacity
                          activeOpacity={0.8}
                          onPress={onPressLink}
                          style={styles.linkPressable}>
                          {renderInstructionLink()}
                        </TouchableOpacity>
                      </Stack>

                      <Stack>
                        <Text
                          fontWeight={'700'}
                          fontSize={scale({ phone: 10, tablet: 12 })}
                          fontFamily="Inter"
                          style={styles.skillItemText}>
                          2. {STRINGS.playerSession.stepDetail.skill2Label}
                        </Text>
                        <TouchableOpacity
                          activeOpacity={0.8}
                          onPress={onPressLink}
                          style={styles.linkPressable}>
                          {renderInstructionLink()}
                        </TouchableOpacity>
                      </Stack>
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          </ScrollView>
        </Stack>
      </SheetContent>
    </SheetModal>
  );
}

const useStyles = createStyles(({ scale }) => ({
  sheet: {
    backgroundColor: colors['3'],
  },
  sheetPhone: {
    height: '92%',
  },
  sheetContainer: {
    flex: 1,
  },
  sheetHeader: {
    paddingHorizontal: scale(20),
    paddingTop: scale(35),
    paddingBottom: scale(16),
    backgroundColor: colors['0'],
  },
  backButton: {
    width: scale(60),
    alignItems: 'center',
    justifyContent: 'center',
  },
  backText: {
    color: colors.Blue,
    textTransform: 'uppercase',
  },
  title: {
    color: colors.Blue,
    textTransform: 'uppercase',
  },
  headerRightSpacer: {
    width: scale(60),
  },
  divider: {
    height: 1,
    backgroundColor: colors['3'],
  },
  scrollView: {
    flex: 1,
    backgroundColor: colors['3'],
  },
  bodyContent: {
    flexGrow: 1,
    minHeight: '100%',
    paddingHorizontal: scale(18),
    paddingTop: scale({ phone: 12, tablet: 16 }),
    paddingBottom: scale(28),
  },
  levelHeader: {
    color: colors.Blue,
    textTransform: 'uppercase',
    marginBottom: scale(14),
  },
  sectionTitle: {
    color: colors['6'],
  },
  bodyRow: {
    width: '100%',
  },
  leftStripe: {
    width: scale(8),
    borderTopLeftRadius: scale(2),
    borderBottomLeftRadius: scale(2),
  },
  rightContent: {
    paddingLeft: 0,
  },
  card: {
    backgroundColor: colors['0'],
    borderTopRightRadius: scale(2),
    borderBottomRightRadius: scale(2),
    shadowColor: '#000',
    shadowOpacity: 0.12,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
    overflow: 'hidden',
  },
  topicText: {
    color: colors['6'],
  },
  cardDivider: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: colors['10'],
  },
  skillCardTitle: {
    color: colors.Blue,
    textTransform: 'none',
  },
  skillCardBody: {
    color: colors['6'],
  },
  skillItemText: {
    color: colors['6'],
  },
  linkText: {
    color: colors['6'],
    textDecorationLine: 'underline',
  },
  linkPressable: {
    alignSelf: 'flex-start',
  },
  skillItemSubLink: {
    color: colors['6'],
    fontSize: scale(9),
  },
}));
